import { Link } from 'react-router-dom';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { formatDateTime } from '../../lib/dateFormat';
import { agreement, mislabelCandidates, type ClusterResponse } from './insights';
import { HelpTip } from '../../components/HelpTip';

export function ClusterHealthCard({ item, pages }: { item: ClusterResponse['item']; pages: ClusterResponse['pages'] }) {
  const agree = agreement(pages, item.categoryName);
  const mislabels = mislabelCandidates(pages, item.categoryName).length;
  const outliers = pages.filter((p) => p.isOutlier).length;

  return (
    <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Cluster Health</h2>}>
      <DescriptionList>
        <DescriptionTerm>Category</DescriptionTerm>
        <DescriptionDetails>
          <Link
            to={`/categories/${item.categoryId}`}
            className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
          >
            {item.categoryName}
          </Link>
        </DescriptionDetails>
        <DescriptionTerm>Pages</DescriptionTerm>
        {/* Use the live joined member count (same denominator as Agreement). It can
            be below the frozen item.pageCount after a remove, until the next retrain. */}
        <DescriptionDetails>{agree.total}</DescriptionDetails>
        <DescriptionTerm>
          Agreement{' '}
          <HelpTip text="Share of members whose top classification is this cluster's category. Low agreement flags drift or mislabels." />
        </DescriptionTerm>
        <DescriptionDetails>
          {agree.agree}/{agree.total} ({(agree.rate * 100).toFixed(0)}%)
        </DescriptionDetails>
        <DescriptionTerm>
          Mislabel candidates{' '}
          <HelpTip text="Members whose top classification is a different category — likely mislabels to relabel or remove." />
        </DescriptionTerm>
        <DescriptionDetails className={mislabels > 0 ? 'text-amber-600 dark:text-amber-400' : undefined}>
          {mislabels}
        </DescriptionDetails>
        <DescriptionTerm>
          Outliers{' '}
          <HelpTip text="Members far from the centroid (distance > mean + 2σ) — often off-topic pages dragging the cluster." />
        </DescriptionTerm>
        <DescriptionDetails>{outliers}</DescriptionDetails>
        <DescriptionTerm>
          Cohesion{' '}
          <HelpTip text="Average cosine similarity of member pages to the cluster centroid (0–1). Higher = tighter, more self-similar." />
        </DescriptionTerm>
        <DescriptionDetails>{item.cohesion.toFixed(3)}</DescriptionDetails>
        <DescriptionTerm>
          Outlier threshold{' '}
          <HelpTip text="Distance cutoff (mean + 2σ of member distances) above which a member is flagged an outlier. Based on the centroid from last training." />
        </DescriptionTerm>
        <DescriptionDetails>{item.outlierThreshold == null ? '—' : item.outlierThreshold.toFixed(3)}</DescriptionDetails>
        <DescriptionTerm>Created</DescriptionTerm>
        <DescriptionDetails>{formatDateTime(item.createdAt)}</DescriptionDetails>
        <DescriptionTerm>Updated</DescriptionTerm>
        <DescriptionDetails>{formatDateTime(item.updatedAt)}</DescriptionDetails>
      </DescriptionList>
    </Card>
  );
}
